export default function ArqHero() {
  return (
    <section className="arq-radial-tl relative min-h-screen flex items-center pt-32 md:pt-32 pb-20 px-6 overflow-hidden">
      {/* Subtle right-side grid */}
      <div
        aria-hidden
        className="hidden md:block absolute right-0 top-0 bottom-0 w-[45%] opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--arq-blue)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--arq-blue)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 70% 50%, black 30%, transparent 75%)",
        }}
      />

      <div className="max-w-[1200px] mx-auto w-full relative z-10">
        <div className="grid md:grid-cols-[55%_40%] md:gap-[5%] items-center">
          <div>
            <div
              className="arq-label mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "0ms" }}
            >
              AI Security for Regulated Industries
            </div>

            <h1
              className="font-display font-bold text-arq-text leading-[1.1] tracking-[-0.02em] mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(40px, 6.5vw, 64px)", animationDelay: "0ms" }}
            >
              Your AI Is Already Deployed.
              <br />
              <span className="text-arq-blue">Is It Safe?</span>
            </h1>

            <p
              className="text-arq-muted mb-6 max-w-[580px] opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(17px, 1.6vw, 20px)", lineHeight: 1.6, animationDelay: "150ms" }}
            >
              ArqeraAI helps regulated organizations find, prove, and fix vulnerabilities in their
              AI systems, web applications, and data environments before regulators, competitors, or malicious actors do.
            </p>

            <div
              className="flex flex-wrap gap-2 mt-2 mb-2 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "200ms" }}
            >
              {["HIPAA", "SOC 2", "CMMC", "HITRUST", "NIST AI RMF"].map(f => (
                <span key={f} className="arq-tag font-mono text-[11px] text-arq-teal border-arq-teal/40">{f}</span>
              ))}
            </div>

            <a
              href="/resources"
              className="text-[13px] text-arq-teal underline underline-offset-2 hover:text-arq-blue transition-colors mb-4 inline-block opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "250ms" }}
            >
              Download the 2026 HIPAA AI Risk Checklist →
            </a>

            <div
              className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "300ms" }}
            >
              <button
                onClick={() => {
                  // [CONFIRM] Replace with Calendly URL when available
                  const CALENDLY_URL: string | null = null;
                  if (CALENDLY_URL) {
                    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
                  } else {
                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="bg-arq-blue text-white font-semibold text-[15px] px-8 py-4 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 tracking-[0.01em]"
              >
                Schedule Your Free Assessment
              </button>
              <button
                onClick={() => document.querySelector("#how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent text-arq-text font-medium text-[15px] px-2 py-4 hover:underline underline-offset-4 transition-all duration-150"
              >
                See How It Works
              </button>
            </div>

            <div
              className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-arq-tertiary opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "450ms" }}
            >
              <span>No commitment required</span>
              <span>·</span>
              <span>Written proposal within 48 hours</span>
              <span>·</span>
              <span>Trusted by HIPAA, SOC 2 &amp; CMMC-regulated teams</span>
            </div>
          </div>

          {/* Right column - AI Risk Report card mock (md+) */}
          <div
            className="hidden md:block opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
            style={{ animationDelay: "450ms" }}
          >
            <div className="bg-arq-surface1 border border-arq-border rounded-xl p-6 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
              <div className="flex items-center justify-between mb-5">
                <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-arq-muted">
                  AI Risk Report
                </div>
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-arq-danger" />
                  <span className="w-2 h-2 rounded-full" style={{ background: "#D97706" }} />
                  <span className="w-2 h-2 rounded-full bg-arq-teal" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-md bg-arq-bg/60 border border-arq-border">
                  <span className="text-arq-text text-[14px]">Model Integrity</span>
                  <span className="font-mono text-[12px] flex items-center gap-1.5 text-arq-teal">
                    <span>✓</span> Passed
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md bg-arq-bg/60 border border-arq-border">
                  <span className="text-arq-text text-[14px]">Data Pipeline Exposure</span>
                  <span className="font-mono text-[12px] flex items-center gap-1.5" style={{ color: "#D97706" }}>
                    <span>⚠</span> At Risk
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-md bg-arq-bg/60 border border-arq-border">
                  <span className="text-arq-text text-[14px]">Regulatory Alignment</span>
                  <span className="font-mono text-[12px] flex items-center gap-1.5" style={{ color: "#C8402A" }}>
                    <span>✗</span> 2 Gaps
                  </span>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-arq-border font-mono text-[11px] text-arq-tertiary tracking-[0.05em]">
                ArqeraAI Assessment · 3 critical findings
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
